package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LChip
import dev.lingvo.site.ds.LFilterIcon
import dev.lingvo.site.ds.LProgressBar
import dev.lingvo.site.ds.LSearchField
import dev.lingvo.site.ds.Space
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr

internal const val KEY_HEADER_ID = "lingvo-editor-key-header"

@Composable
internal fun CompactQueueHeader(
    state: TranslationEditorState,
    queueMode: EditorQueueMode,
    onOpenFilters: (EditorFocusRef) -> Unit,
    onOpenBulkConfirm: (EditorFocusRef) -> Unit = {},
) {
    val filePath = state.project?.files
        ?.firstOrNull { it.id == state.fileId }
        ?.path
    val filters = appliedEditorFilters(state.currentQuery, filePath)
    val filterCount = filters.size
    val filterLabel = if (filterCount == 0) {
        t("editor.compact_queue_header.filters", "Filters")
    } else {
        tf(
            "editor.compact_queue_header.filters_count",
            "Filters ({count})",
            "count" to filterCount.toString(),
        )
    }
    val narrowed = filters.isNotEmpty() || state.search.isNotBlank()

    Column(
        CompactQueueHeaderStyle.toModifier()
            .attr("id", KEY_HEADER_ID),
    ) {
        Row(
            Modifier.width(100.percent).gap(Space.XS),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LSearchField(
                value = state.search,
                onValueChange = { state.search = it },
                modifier = EditorQueueEssentialBoundaryStyle.toModifier()
                    .attr("id", EDITOR_SEARCH_ID)
                    .attr("data-editor-search", "true"),
                label = t("editor.compact_queue_header.search_translation_keys_and_source_text", "Search keys and source text"),
                placeholder = t("editor.compact_queue_header.search_keys", "Search keys"),
                shortcutHint = "/",
            )
            LButton(
                label = filterLabel,
                onClick = {
                    onOpenFilters(EditorFocusRef.ElementId(EDITOR_FILTERS_TRIGGER_ID))
                },
                modifier = Modifier
                    // **The label grows by three characters and the button must not wrap.** With a
                    // filter applied the label becomes "Filters (1)", and beside a search field
                    // that is `flex-grow: 1; min-width: 0` this button lost the negotiation: it
                    // broke across two lines, stranding "(1)" under "Filters" with the glyph
                    // beside the first line only, and stopped matching the 40px control height of
                    // the field next to it. Fixed here rather than in `LButtonStyle` so no other
                    // SM button in the product has to be re-measured.
                    .flexShrink(0)
                    .attr("id", EDITOR_FILTERS_TRIGGER_ID)
                    .attr("aria-expanded", (queueMode == EditorQueueMode.FILTERS).toString()),
                variant = LButtonVariant.SECONDARY,
                size = LButtonSize.SM,
                leadingIcon = { LFilterIcon() },
                accessibleLabel = filterLabel,
            )
        }
        // **Nothing rather than a confident zero.** `progress.total` is 0 until the project lands,
        // so a tile drawn now would announce, for a second or so, that a locale needing forty
        // translations needs none. The counting sentence is the honest reading of the same state.
        if (state.project == null) {
            Span(
                CompactQueueProgressStyle.toModifier().toAttrs {
                    attr("aria-label", tr("editor.compact_queue_header.translation_progress", "Translation progress"))
                },
            ) {
                Text(editorQueueCountsPending())
            }
        } else {
            EditorQueueActions(
                state = state,
                filtered = narrowed,
                onOpenBulkConfirm = onOpenBulkConfirm,
            )
            // The bar carries the shape of the work; the tiles above carry the numbers. `compact`
            // drops the bar's own captions so it does not say either of them a second time.
            LProgressBar(
                progress = state.progress,
                modifier = Modifier.width(100.percent),
                label = t("editor.compact_queue_header.translation_progress", "Translation progress"),
                compact = true,
            )
        }
        if (filters.isNotEmpty()) {
            Row(CompactQueueChipsStyle.toModifier()) {
                filters.forEach { filter ->
                    LChip(
                        text = filter.label,
                        modifier = Modifier.styleModifier { property("flex", "0 0 auto") },
                        onRemove = { state.clearFilter(filter.id) },
                        removeLabel = tf(
                            "editor.compact_queue_header.remove_filter",
                            "Remove filter {filter}",
                            "filter" to filter.label,
                        ),
                    )
                }
            }
        }
    }
}
